import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "em_theme";
const VALID = ["light", "dark", "system"];

function getSystemDark() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

function getInitialTheme() {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (VALID.includes(stored)) return stored;
  return "system"; // new users follow OS by default
}

function applyTheme(theme) {
  const root = document.documentElement;
  const dark = theme === "dark" || (theme === "system" && getSystemDark());
  root.classList.toggle("dark", dark);
}

/**
 * Tri-state theme hook: "light" | "dark" | "system".
 * - Persists explicit choice in localStorage.
 * - In "system" mode, tracks `prefers-color-scheme` media query live.
 */
export default function useTheme() {
  const [theme, setTheme] = useState(getInitialTheme);

  // Apply + persist on change
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Follow OS live when in system mode
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [theme]);

  // Cycle Light → Dark → System → Light
  const cycle = useCallback(() => {
    setTheme((t) => (t === "light" ? "dark" : t === "dark" ? "system" : "light"));
  }, []);

  // Kept for legacy callers
  const toggle = cycle;
  const isDark = theme === "dark" || (theme === "system" && getSystemDark());

  return { theme, setTheme, cycle, toggle, isDark };
}

// Apply on module load so first paint matches the user's preference (no flash)
if (typeof document !== "undefined") {
  applyTheme(getInitialTheme());
}
